import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import flags from '@swf/core/assets/flags';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import { countryInitialName, getCountryCode } from '@swf/core/utils/phoneVerification';

import { CashierFlowType } from '../../types/Cashier';
import ListItem from '../ListItem/ListItem';

import { TXTCLI_Code } from './DialingCode.messages.json';
import { avatar, root, wrapper } from './DialingCode.scss';
import DialingCodeSelect from './components/DialingCodeSelect/DialingCodeSelect';

interface CountryCode {
  text: string;
  value: string;
}

interface Props {
  fieldName?: string;
  setCallingCode?: (arg1: string) => void;
  inputNumberName?: string;
}

const DialingCode: React.FC<Props> = ({
  fieldName = 'dialingCode',
  inputNumberName,
  setCallingCode
}) => {
  const form = useForm();
  const { values } = form.getState();
  const userCountry = useSelector(select.account.getCountry);
  const site = useSelector(select.cms.getSiteId);
  const [countryCode, setCountryCode] = useState<CountryCode>();
  const {
    data: { cashierFlow }
  } = useWizardContext();
  const isSWCWithdrawFlow = cashierFlow === CashierFlowType.FirstWithdraw;
  const isDialingCodeDisabled =
    isSWCWithdrawFlow || [Sites.UK, Sites.USMI, Sites.DE, Sites.US_NJ, Sites.US_PA].includes(site);

  const country = useMemo(() => {
    if (fieldName === 'dialingCode' && values.dialingCode && site !== Sites.UK) {
      return values.dialingCode;
    }
    if (fieldName === 'occupationDialingCode' && values.occupationDialingCode) {
      return values.occupationDialingCode;
    }
    if (
      fieldName === 'occupationDialingCode' &&
      values.occupationCompanyPhone &&
      !values.occupationDialingCode
    ) {
      return countryInitialName(values.occupationCompanyPhone, userCountry);
    }
    if (userCountry) {
      return userCountry;
    }
    return values?.country;
  }, [fieldName, values, userCountry, site]);

  useEffect(() => {
    const countryCode = getCountryCode(country);
    form.change(fieldName, country);
    setCountryCode(countryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, fieldName, isSWCWithdrawFlow]);

  const handleClick = (countryValue: string) => {
    if (inputNumberName) form?.change(inputNumberName, '');
    if (fieldName === 'dialingCode') form?.change('phoneNumber', '');
    if (fieldName === 'occupationDialingCode') form?.change('occupationCompanyPhone', '');
    if (setCallingCode) setCallingCode(country);
    setCountryCode(getCountryCode(countryValue));
  };

  if (!countryCode?.value && !countryCode?.text) {
    return null;
  }

  return (
    <div className={wrapper}>
      <label htmlFor={fieldName}>
        <Translation {...TXTCLI_Code} />
      </label>
      <DialingCodeSelect
        label={TXTCLI_Code}
        onClick={handleClick}
        fieldName={fieldName}
        disabled={isDialingCodeDisabled}
      >
        <ListItem
          primary={countryCode.text}
          avatarURL={flags[countryCode.value]}
          classes={{ avatar, root }}
        />
      </DialingCodeSelect>
    </div>
  );
};

export default DialingCode;
